import { loadToken, saveToken, saveActiveOrg } from './http';
import { getOrCreateDeviceId } from './deviceId';
import { can } from '../iam/can';
import { applyBranding } from '../branding/apply';

// Apply the white-label (PLS) branding carried by an auth payload, if any, so
// logged-in sessions pick up the tenant logo/colors (the public /api/branding
// endpoint is skipped once a session exists).
function applySessionBranding(commit, data) {
  if (data && data.branding) {
    commit('branding/SET_BRANDING', data.branding, { root: true });
    applyBranding(data.branding);
  }
}

const USER_KEY = 'mu.auth.user';
const PERMS_KEY = 'mu.auth.permissions';
const ORGS_KEY = 'mu.auth.organizations';
const ORG_KEY = 'mu.auth.currentOrg';
const ORG_SELECTED_KEY = 'mu.orgSelected';
// Impersonation: snapshot of the impersonator's own session so it can be
// restored client-side when impersonation ends (the backend only revokes the
// impersonation token; the original token stays valid).
const IMPERSONATION_KEY = 'mu.auth.impersonation';
// Organization impersonation (portal-app parity): the backend keeps the same
// token and just flags it, so we only remember the impersonated-org context to
// render the banner and offer "stop impersonating organization".
const ORG_IMPERSONATION_KEY = 'mu.auth.orgImpersonation';

function loadJson(key) {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch (_e) { return null; }
}
function saveJson(key, value) {
  try { value == null ? localStorage.removeItem(key) : localStorage.setItem(key, JSON.stringify(value)); } catch (_e) {}
}
function loadOrgId() {
  try { const v = localStorage.getItem(ORG_KEY); return v ? Number(v) : null; } catch (_e) { return null; }
}
function clearOrgSelected() {
  try { sessionStorage.removeItem(ORG_SELECTED_KEY); } catch (_e) {}
}
function markOrgSelected() {
  try { sessionStorage.setItem(ORG_SELECTED_KEY, '1'); } catch (_e) {}
}
/** Pick the active org id from a memberships list (is_active flag) or fallback. */
function activeOrgFrom(organizations, fallback) {
  if (Array.isArray(organizations)) {
    const active = organizations.find((o) => o.is_active);
    if (active) return active.id;
  }
  return fallback != null ? fallback : null;
}

/**
 * Vuex 3 module factory. The factory receives the http (axios) instance so
 * the module can talk to /api/auth/* without a circular dependency.
 *
 *    import { createAuthModule } from '@shared/auth/store';
 *    store.registerModule('auth', createAuthModule(http));
 */
export function createAuthModule(http) {
  return {
    namespaced: true,
    state: () => ({
      token:                 loadToken(),
      user:                  loadJson(USER_KEY),
      permissions:           loadJson(PERMS_KEY) || [],
      organizations:         loadJson(ORGS_KEY) || [],
      currentOrganizationId: loadOrgId(),
      impersonation:         loadJson(IMPERSONATION_KEY),
      orgImpersonation:      loadJson(ORG_IMPERSONATION_KEY),
      loading:               false,
      error:                 null,
    }),
    getters: {
      isLoggedIn: (s) => !!s.token && !!s.user,
      user:       (s) => s.user,
      token:      (s) => s.token,
      permissions:(s) => s.permissions,
      can:        (s) => (action, resource) => can(s.permissions, action, resource),
      organizations:         (s) => s.organizations,
      currentOrganizationId: (s) => s.currentOrganizationId,
      currentOrganization:   (s) => (s.organizations || []).find((o) => o.id === s.currentOrganizationId) || null,
      isImpersonating:       (s) => !!s.impersonation,
      impersonator:          (s) => (s.impersonation ? s.impersonation.impersonator : null),
      isImpersonatingOrganization: (s) => !!s.orgImpersonation,
      impersonatedOrganization:    (s) => s.orgImpersonation || null,
    },
    mutations: {
      SET_LOADING(state, v) { state.loading = !!v; },
      SET_ERROR(state, err) { state.error = err || null; },
      SET_SESSION(state, { token, user, permissions, organizations }) {
        state.token = token || null;
        state.user = user || null;
        state.permissions = Array.isArray(permissions) ? permissions : [];
        saveToken(state.token);
        saveJson(USER_KEY, state.user);
        saveJson(PERMS_KEY, state.permissions);

        if (organizations !== undefined) {
          state.organizations = Array.isArray(organizations) ? organizations : [];
          saveJson(ORGS_KEY, state.organizations);
          state.currentOrganizationId = activeOrgFrom(
            state.organizations,
            user ? user.organization_id : state.currentOrganizationId,
          );
          saveActiveOrg(state.currentOrganizationId);
        }
      },
      SET_ORGANIZATIONS(state, organizations) {
        state.organizations = Array.isArray(organizations) ? organizations : [];
        saveJson(ORGS_KEY, state.organizations);
        state.currentOrganizationId = activeOrgFrom(state.organizations, state.currentOrganizationId);
        saveActiveOrg(state.currentOrganizationId);
      },
      SET_ACTIVE_ORG(state, orgId) {
        state.currentOrganizationId = orgId != null ? Number(orgId) : null;
        saveActiveOrg(state.currentOrganizationId);
      },
      SET_IMPERSONATION(state, impersonation) {
        state.impersonation = impersonation || null;
        saveJson(IMPERSONATION_KEY, state.impersonation);
      },
      SET_ORG_IMPERSONATION(state, orgImpersonation) {
        state.orgImpersonation = orgImpersonation || null;
        saveJson(ORG_IMPERSONATION_KEY, state.orgImpersonation);
      },
      CLEAR_SESSION(state) {
        state.token = null;
        state.user = null;
        state.permissions = [];
        state.organizations = [];
        state.currentOrganizationId = null;
        state.impersonation = null;
        saveToken(null);
        saveJson(USER_KEY, null);
        saveJson(PERMS_KEY, null);
        saveJson(ORGS_KEY, null);
        saveJson(IMPERSONATION_KEY, null);
        saveJson(ORG_IMPERSONATION_KEY, null);
        state.orgImpersonation = null;
        saveActiveOrg(null);
        clearOrgSelected();
      },
    },
    actions: {
      async login({ commit }, { username, password, force = false }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
          const { data } = await http.post('/api/auth/login', {
            username,
            password,
            device_id: getOrCreateDeviceId(),
            force,
          });
          commit('SET_SESSION', {
            token: data.token,
            user:  data.user,
            permissions: data.permissions || [],
            organizations: data.organizations || [],
          });
          applySessionBranding(commit, data);
          // Force the org selector to appear again for this new session.
          clearOrgSelected();
          return data;
        } catch (e) {
          // BR-002: an active session on another device. Surface the conflict to
          // the view so it can prompt the user instead of showing a plain error.
          const res = e && e.response;
          if (res && res.status === 409 && res.data && res.data.code === 'SESSION_CONFLICT') {
            const conflict = new Error(res.data.message || 'You are already signed in on another device.');
            conflict.sessionConflict = true;
            conflict.existing = res.data.existing || null;
            throw conflict;
          }
          const msg = (res && res.data && res.data.message) || 'Unable to sign in.';
          commit('SET_ERROR', msg);
          throw new Error(msg);
        } finally {
          commit('SET_LOADING', false);
        }
      },
      async oauthLogin({ commit }, { provider, credential, force = false }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
          const { data } = await http.post(`/api/auth/oauth/${provider}`, {
            ...credential,
            device_id: getOrCreateDeviceId(),
            force,
          });
          if (data && data.token) {
            commit('SET_SESSION', {
              token: data.token,
              user:  data.user,
              permissions: data.permissions || [],
              organizations: data.organizations || [],
            });
            applySessionBranding(commit, data);
            clearOrgSelected();
            return data;
          }
          // No token → cuenta pendiente de aprobación por un administrador.
          return { pending: true, message: (data && data.message) || 'Your account is pending administrator approval.' };
        } catch (e) {
          // BR-002: mirror login()'s single-session conflict handling.
          const res = e && e.response;
          if (res && res.status === 409 && res.data && res.data.code === 'SESSION_CONFLICT') {
            const conflict = new Error(res.data.message || 'You are already signed in on another device.');
            conflict.sessionConflict = true;
            conflict.existing = res.data.existing || null;
            throw conflict;
          }
          const msg = (res && res.data && res.data.message) || 'Unable to sign in.';
          commit('SET_ERROR', msg);
          throw new Error(msg);
        } finally {
          commit('SET_LOADING', false);
        }
      },
      async fetchMe({ commit, state }) {
        if (!state.token) return null;
        const { data } = await http.get('/api/auth/me');
        commit('SET_SESSION', {
          token: state.token,
          user:  data.user,
          permissions: data.permissions || [],
          organizations: data.organizations || [],
        });
        commit('SET_ORG_IMPERSONATION', data.impersonated_organization || null);
        applySessionBranding(commit, data);
        return data;
      },
      async refreshPermissions({ commit, state }) {
        if (!state.token) return;
        const { data } = await http.get('/api/auth/permissions');
        commit('SET_SESSION', {
          token: state.token,
          user:  state.user,
          permissions: data.permissions || [],
        });
      },
      async fetchOrganizations({ commit, state }) {
        if (!state.token) return [];
        const { data } = await http.get('/api/auth/organizations');
        commit('SET_ORGANIZATIONS', data.organizations || []);
        return data.organizations || [];
      },
      // Multi-org: switch the active organization for this session (per token).
      // Recomputes user + permissions for the new org context server-side.
      async switchOrganization({ commit, state }, organizationId) {
        const { data } = await http.post('/api/auth/switch-organization', {
          organization_id: organizationId,
        });
        commit('SET_SESSION', {
          token: state.token,
          user:  data.user,
          permissions: data.permissions || [],
          organizations: data.organizations || [],
        });
        applySessionBranding(commit, data);
        markOrgSelected();
        return data;
      },
      async logout({ commit, state }, payload = {}) {
        if (state.token && !payload.silent) {
          try { await http.post('/api/auth/logout'); } catch (_e) {}
        }
        commit('CLEAR_SESSION');
      },
      // Impersonation: swap the current session for a full session of the target
      // user, snapshotting the impersonator's own session so it can be restored
      // when impersonation ends. Refused server-side if already impersonating.
      async impersonate({ commit, state }, targetUserId) {
        const original = {
          token: state.token,
          user: state.user,
          permissions: state.permissions,
          organizations: state.organizations,
        };
        const { data } = await http.post(`/api/auth/impersonate/${targetUserId}`);
        commit('SET_SESSION', {
          token: data.token,
          user:  data.user,
          permissions: data.permissions || [],
          organizations: data.organizations || [],
        });
        applySessionBranding(commit, data);
        commit('SET_IMPERSONATION', {
          impersonator: (data.impersonation && {
            id: data.impersonation.impersonator_id,
            name: data.impersonation.impersonator_name,
          }) || null,
          original,
        });
        markOrgSelected();
        return data;
      },
      // End impersonation: revoke the impersonation token server-side and restore
      // the impersonator's original session client-side.
      async stopImpersonating({ commit, state }) {
        if (!state.impersonation) return null;
        const original = state.impersonation.original;
        try { await http.post('/api/auth/stop-impersonating'); } catch (_e) {}
        commit('SET_IMPERSONATION', null);
        if (original && original.token) {
          commit('SET_SESSION', {
            token: original.token,
            user:  original.user,
            permissions: original.permissions || [],
            organizations: original.organizations || [],
          });
          markOrgSelected();
        } else {
          commit('CLEAR_SESSION');
        }
        return original;
      },
      // Organization impersonation (portal-app parity): operate the portal as a
      // descendant organization of your subtree. The backend keeps the SAME
      // token and just flags it, then returns a payload recomputed for the
      // impersonated org (permissions + branding).
      async searchImpersonableOrganizations(_ctx, query = '') {
        const { data } = await http.get('/api/auth/impersonate-organization/search', {
          params: { q: query || '' },
        });
        return (data && data.organizations) || [];
      },
      async startOrganizationImpersonation({ commit, state }, organizationId) {
        const { data } = await http.post('/api/auth/impersonate-organization', {
          organization_id: organizationId,
        });
        commit('SET_SESSION', {
          token: state.token,
          user:  data.user,
          permissions: data.permissions || [],
          organizations: data.organizations || [],
        });
        commit('SET_ORG_IMPERSONATION', data.impersonated_organization || null);
        applySessionBranding(commit, data);
        markOrgSelected();
        return data;
      },
      async stopOrganizationImpersonation({ commit, state }) {
        if (!state.orgImpersonation) return null;
        const { data } = await http.delete('/api/auth/impersonate-organization');
        commit('SET_SESSION', {
          token: state.token,
          user:  data.user,
          permissions: data.permissions || [],
          organizations: data.organizations || [],
        });
        commit('SET_ORG_IMPERSONATION', null);
        applySessionBranding(commit, data);
        markOrgSelected();
        return data;
      },
    },
  };
}
